import { Folder } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { isAccountRoot, pathSegments, folderPathTitle } from '../../../lib/file-browser';
import { draftFileName, MAX_HINT_CHARS } from '../../../lib/file-draft';
import { Button, Input, Typography } from '../ui';
import { FilesSpaceMark } from '../files/FilesSpaceMark';
import styles from './DocDraftFields.module.css';

// The three things a new file needs that an existing one already has: what it is called, what it is for,
// and where it goes. They sit at the TOP of the editor, above the body, because the editor is now the whole
// create surface — the `+` used to open a small dialog for exactly these and then hand over to this screen.
//
// The type picker that dialog had is gone: the name carries its own extension (`draftFileName` appends
// `.md` when it has none), which is how the file is named everywhere else it appears. A segmented control
// for three suffixes was a lot of chrome for something the name already says.
export function DocDraftFields({
  name,
  onName,
  description,
  onDescription,
  folder,
  workspaceNames,
  onPickFolder,
  disabled,
  creating,
}: {
  name: string;
  onName: (value: string) => void;
  description: string;
  onDescription: (value: string) => void;
  /** `''` until a destination is chosen — the top of the tree is a list of Spaces, not a place. */
  folder: string;
  workspaceNames: Record<string, string>;
  /** Absent on a surface that cannot host the picker — the destination is then read-only. */
  onPickFolder?: () => void;
  disabled?: boolean;
  /** A NEW file — the preview line says what will be created. An existing one is being renamed. */
  creating?: boolean;
}) {
  const { t } = useTranslation();
  const fileName = draftFileName(name);

  // The destination as a reader would say it: the Space by its NAME, then the folders under it, and the
  // account roots translated — `brain` is "agents". Same rule the breadcrumb and the move dialog use.
  const segments = pathSegments(folder);
  const destination = folder ? folderPathTitle(folder, workspaceNames, t) : '';
  // A Space is NOT a folder — it has an identity, an icon and a team, which is exactly the distinction
  // `FolderBrowseList` draws in its rows. Giving it the folder glyph here said the opposite. The account
  // roots (agents, skills) ARE folders Symphony made, so they keep the glyph.
  const spaceId = folder && !isAccountRoot(segments[0]) ? segments[0] : '';

  return (
    <div className={styles.fields}>
      <Input
        size="medium"
        value={name}
        onChange={(event) => onName(event.target.value)}
        disabled={disabled}
        label={t('files.new.nameLabel')}
        placeholder={t('files.new.namePlaceholder')}
      />

      <Input
        size="medium"
        value={description}
        onChange={(event) => onDescription(event.target.value)}
        disabled={disabled}
        label={t('files.new.descriptionLabel')}
        optionalLabel={t('common.optional')}
        // The (i). A description is not a summary for a human — it is the line the agent reads to decide
        // whether this file is worth opening, so the tooltip says that rather than "describe the file".
        labelInfo={t('files.new.descriptionInfo')}
        placeholder={t('files.new.descriptionPlaceholder')}
        // Capped at the field's OWN limit, with the DS counter, so the route's `hint_too_long` is
        // unreachable from here. The alternative was a new error string in 37 bundles for a refusal the
        // user cannot act on after the fact — a field that stops accepting, and says how much is left,
        // is the better affordance and needs no copy at all. The 400 stays as the server-side guard.
        maxLength={MAX_HINT_CHARS}
        showCount
      />

      <div className={styles.destination}>
        <Typography variant="p3" color="secondary" component="span" className={styles.destinationLabel}>
          {t('files.new.locationLabel')}
        </Typography>
        <div className={styles.destinationRow}>
          {spaceId ? (
            <FilesSpaceMark id={spaceId} name={workspaceNames[spaceId] ?? spaceId} size={20} />
          ) : (
            <Folder size={16} aria-hidden className={styles.glyph} />
          )}
          <Typography variant="p2" component="span" className={styles.destinationValue}>
            {destination || t('files.move.pickWorkspace')}
          </Typography>
          {onPickFolder && (
            <Button variant="text" size="small" onClick={onPickFolder} disabled={disabled}>
              {t('files.new.changeLocation')}
            </Button>
          )}
        </div>
        {/* What will actually be written, spelled out — the name field accepts `/` and silently gains an
            extension, so the reader should not have to work the result out. */}
        {creating && folder && fileName && (
          <Typography variant="p3" color="secondary" component="p">
            {t('files.new.willCreate', { path: `${destination} / ${fileName}` })}
          </Typography>
        )}
      </div>
    </div>
  );
}
